'use client';

import React from 'react';
import { Filter } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { CHARACTER_ROLE_TYPES } from '@/types/enums';
import { normalizeRoles } from '@/integrity/iam/permissions';

interface RoleFilterModalProps {
  userRoles: string[];
  visibleRoles: string[];
  setVisibleRoles: React.Dispatch<React.SetStateAction<string[]>>;
  showLockedRoles: boolean;
  setShowLockedRoles: React.Dispatch<React.SetStateAction<boolean>>;
  showBaseLessons: boolean;
  setShowBaseLessons: React.Dispatch<React.SetStateAction<boolean>>;
  triggerClassName?: string;
  showLabel?: boolean;
}

export function RoleFilterModal({
  userRoles,
  visibleRoles,
  setVisibleRoles,
  showLockedRoles,
  setShowLockedRoles,
  showBaseLessons,
  setShowBaseLessons,
  triggerClassName = '',
  showLabel = true,
}: RoleFilterModalProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className={`inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-border/60 bg-background/80 px-1.5 text-[10px] font-medium text-muted-foreground transition-colors hover:border-primary/30 hover:bg-muted/60 hover:text-foreground ${triggerClassName}`}
        >
          <Filter className="w-3.5 h-3.5" />
          {showLabel ? <span className="hidden md:inline">Roles</span> : null}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-background">
        <DialogHeader>
          <DialogTitle>Filter by Role</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between py-3 border-b border-border/50">
          <Label htmlFor="show-locked" className="text-sm font-medium text-foreground">
            Show locked roles
          </Label>
          <Switch
            id="show-locked"
            checked={showLockedRoles}
            onCheckedChange={setShowLockedRoles}
          />
        </div>
        <div className="flex items-center justify-between py-3 border-b border-border/50">
          <Label htmlFor="show-base-lessons" className="text-sm font-medium text-foreground">
            Show base lessons
          </Label>
          <Switch
            id="show-base-lessons"
            checked={showBaseLessons}
            onCheckedChange={setShowBaseLessons}
          />
        </div>
        
        <div className="flex flex-col gap-4 py-4">
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Regular Roles</h4>
            <div className="flex flex-wrap gap-2">
              {CHARACTER_ROLE_TYPES.REGULAR.map(role => {
                const r = role.toLowerCase();
                const hasRole = normalizeRoles(userRoles).includes(r);
                const isVisible = visibleRoles.includes(r);
                
                if (!hasRole) {
                  return (
                    <div key={role} className="px-3 py-1 rounded-full text-xs font-medium bg-muted/30 text-muted-foreground/50 border border-border/30 cursor-not-allowed">
                      {role}
                    </div>
                  );
                }
                
                return (
                  <button
                    key={role}
                    onClick={() => {
                      setVisibleRoles(prev => 
                        prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
                      );
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      isVisible 
                        ? 'bg-zinc-700/50 text-amber-500 border-amber-500 hover:bg-amber-500/20' 
                        : 'bg-primary/10 text-primary border-primary/50 hover:bg-primary/30'
                    }`}
                  >
                    {role}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-border/50 pt-4 mt-2">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Special Roles</h4>
            <div className="flex flex-wrap gap-2">
              {CHARACTER_ROLE_TYPES.SPECIAL.map(role => {
                const r = role.toLowerCase();
                const hasRole = normalizeRoles(userRoles).includes(r);
                
                if (!hasRole) {
                  return null; // Only show special roles the user has
                }
                
                return (
                  <div key={role} className="px-3 py-1 rounded-full text-xs font-medium bg-zinc/50 dark:bg-zinc-800 text-foreground border border-border/50">
                    {role}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
